import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CalendarCheck2, ChartNoAxesColumnIncreasing,
  Clock3, Instagram, Link2, Menu, MessageCircle, PlayCircle,
  Scissors, Store, UsersRound, X,
} from "lucide-react";
import "./LandingPage.css";
import dbhLogo from "@/assets/dbh-logo-source.png";

const navigation = [
  ["Início", "#inicio"],
  ["Para Barbearias", "#barbearias"],
  ["Funcionalidades", "#funcionalidades"],
  ["Planos", "#planos"],
  ["Contato", "#contato"],
];

const benefits = [
  { icon: Clock3, title: "Agendamentos 24h", detail: "Seus clientes agendam quando quiserem" },
  { icon: MessageCircle, title: "Confirmação via WhatsApp", detail: "Mais praticidade, menos faltas" },
  { icon: ChartNoAxesColumnIncreasing, title: "Mais organização", detail: "Sua agenda sempre em dia" },
  { icon: UsersRound, title: "Feito para barbearias", detail: "Simples e direto ao ponto" },
];

const steps = [
  { icon: Store, title: "Cadastre sua barbearia", detail: "Crie sua conta e configure as informações da sua barbearia." },
  { icon: Scissors, title: "Adicione seus serviços e barbeiros", detail: "Defina os serviços, preços, duração e horários." },
  { icon: Link2, title: "Compartilhe seu link", detail: "Divulgue o link da sua página de agendamento para seus clientes." },
  { icon: CalendarCheck2, title: "Receba agendamentos", detail: "Seus clientes escolhem o serviço, barbeiro e horário. Você recebe tudo organizado." },
];

const WHATSAPP_NUMBER = "5538997220136";
const whatsappUrl = (message = "Olá! Vim pelo AgendaCut e gostaria de mais informações.") =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

const plans = [
  { name: "Mensal", price: "R$ 39,90", period: "por mês", monthly: null },
  { name: "Semestral", price: "R$ 220,00", period: "a cada 6 meses", monthly: "Equivale a R$ 36,66/mês" },
  { name: "Anual", price: "R$ 400,00", period: "por ano", monthly: "Equivale a R$ 33,33/mês", featured: true },
];

function Brand() {
  return <a className="ac-brand" href="#inicio" aria-label="AgendaCut — início"><Scissors aria-hidden="true" /><span>Agenda<span>Cut</span></span></a>;
}

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  return <div className="ac-landing">
    <header className="ac-header">
      <div className="ac-shell ac-header-inner">
        <Brand />
        <nav className={`ac-nav ${menuOpen ? "is-open" : ""}`} aria-label="Navegação principal">
          {navigation.map(([label, href]) => <a key={href} href={href} onClick={closeMenu}>{label}</a>)}
          <Link className="ac-mobile-panel" to="/admin/login" onClick={closeMenu}>Acessar painel</Link>
        </nav>
        <Link className="ac-panel-link" to="/admin/login">Acessar painel</Link>
        <button className="ac-menu-toggle" type="button" aria-label={menuOpen ? "Fechar menu" : "Abrir menu"} aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X /> : <Menu />}</button>
      </div>
    </header>

    <main>
      <section className="ac-hero" id="inicio" aria-labelledby="ac-hero-title">
        <div className="ac-hero-photo" aria-hidden="true" />
        <div className="ac-shell ac-hero-content">
          <div className="ac-hero-copy">
            <p className="ac-eyebrow">Simples. Prático. Mais clientes.</p>
            <h1 id="ac-hero-title">Agendamentos online para a sua <span>barbearia</span></h1>
            <p className="ac-hero-description">Organize sua agenda, reduza faltas e receba mais clientes com um sistema de agendamento feito para barbearias.</p>
            <div className="ac-actions">
              <a className="ac-button ac-button-primary" href={whatsappUrl()} target="_blank" rel="noopener noreferrer">Quero usar na minha barbearia <ArrowRight aria-hidden="true" /></a>
              <a className="ac-button ac-button-outline" href="#funcionalidades"><PlayCircle aria-hidden="true" /> Ver como funciona</a>
            </div>
          </div>
          <div className="ac-benefits" id="barbearias" aria-label="Benefícios do AgendaCut">
            {benefits.map(({ icon: Icon, title, detail }) => <div className="ac-benefit" key={title}><div className="ac-benefit-icon"><Icon aria-hidden="true" /></div><div><strong>{title}</strong><span>{detail}</span></div></div>)}
          </div>
        </div>
      </section>

      <section className="ac-how" id="funcionalidades" aria-labelledby="ac-how-title">
        <div className="ac-shell">
          <div className="ac-section-heading"><p className="ac-eyebrow">Em poucos passos</p><h2 id="ac-how-title">Como funciona?</h2><p>Comece a usar o AgendaCut na sua barbearia de forma rápida e fácil.</p></div>
          <div className="ac-steps">{steps.map(({ icon: Icon, title, detail }, index) => <article className="ac-step" key={title}><span className="ac-step-number">{index + 1}</span><Icon className="ac-step-icon" aria-hidden="true" /><h3>{title}</h3><p>{detail}</p></article>)}</div>
        </div>
      </section>

      <section className="ac-value" aria-labelledby="ac-value-title">
        <div className="ac-shell ac-value-inner">
          <div className="ac-value-copy"><p className="ac-eyebrow">Pronto para evoluir sua barbearia?</p><h2 id="ac-value-title">Mais tempo para o que realmente importa.</h2><p>Deixe a tecnologia cuidar da sua agenda, enquanto você foca no seu cliente.</p><div className="ac-actions"><a className="ac-button ac-button-primary" href={whatsappUrl()} target="_blank" rel="noopener noreferrer">Quero começar agora <ArrowRight aria-hidden="true" /></a><a className="ac-button ac-button-outline" href={whatsappUrl()} target="_blank" rel="noopener noreferrer"><MessageCircle aria-hidden="true" /> Falar com a equipe</a></div></div>
          <figure className="ac-testimonial"><span className="ac-quote-mark">“</span><blockquote>O AgendaCut facilitou muito nossa rotina. Os clientes adoraram e a agenda ficou toda organizada.</blockquote><figcaption><span className="ac-avatar">RS</span><span><strong>Rafael Souza</strong><small>Barbearia Navalha · depoimento ilustrativo</small></span></figcaption></figure>
        </div>
      </section>

      <section className="ac-plans" id="planos" aria-labelledby="ac-plans-title">
        <div className="ac-shell">
          <div className="ac-section-heading"><p className="ac-eyebrow">Planos AgendaCut</p><h2 id="ac-plans-title">Escolha o seu plano</h2><p>Uma agenda mais organizada para sua barbearia.</p></div>
          <div className="ac-plan-grid">{plans.map((plan) => <article className={`ac-plan ${plan.featured ? "ac-plan-featured" : ""}`} key={plan.name}>
            <h3>{plan.name}</h3><p className="ac-plan-price">{plan.price}</p><p className="ac-plan-period">{plan.period}</p><p className="ac-plan-monthly">{plan.monthly || "\u00a0"}</p>
            <a className={`ac-button ${plan.featured ? "ac-button-primary" : "ac-button-outline"}`} href={whatsappUrl(`Olá! Vim pelo AgendaCut e tenho interesse no plano ${plan.name}.`)} target="_blank" rel="noopener noreferrer">Tenho interesse <ArrowRight aria-hidden="true" /></a>
          </article>)}</div>
        </div>
      </section>

      <section className="ac-contact" id="contato" aria-labelledby="ac-contact-title"><div className="ac-shell ac-contact-inner"><div><p className="ac-eyebrow">Contato</p><h2 id="ac-contact-title">Fale com a equipe</h2><p>Quer conhecer melhor o AgendaCut ou tirar alguma dúvida? Entre em contato.</p></div><div className="ac-contact-links"><a href={whatsappUrl()} target="_blank" rel="noopener noreferrer"><MessageCircle aria-hidden="true" /><span><small>WhatsApp</small>(38) 9 9722-0136</span></a><a href="mailto:buckfist@hotmail.com"><span className="ac-at" aria-hidden="true">@</span><span><small>E-mail</small>buckfist@hotmail.com</span></a></div></div></section>
    </main>

    <footer className="ac-footer"><div className="ac-shell ac-footer-inner"><div className="ac-footer-brand"><Brand /><p>Barbearias mais organizadas. Clientes mais satisfeitos.</p></div><nav aria-label="Navegação do rodapé">{navigation.map(([label, href]) => <a key={href} href={href}>{label}</a>)}</nav><div className="ac-footer-bottom"><div className="ac-socials" aria-label="Redes sociais"><span title="Instagram em breve"><Instagram aria-hidden="true" /></span><a href={whatsappUrl()} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp AgendaCut"><MessageCircle aria-hidden="true" /></a></div><Link to="/privacidade">Privacidade</Link><Link to="/termos-de-uso">Termos de uso</Link><small>© 2026 AgendaCut. Todos os direitos reservados.</small><div className="ac-credit"><img src={dbhLogo} alt="DBH" /></div></div></div></footer>
  </div>;
}
